import { IonButtons, IonFooter, IonIcon, IonModal, IonText } from "@ionic/react";
import { useDeferredValue, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AutoHeight from "embla-carousel-auto-height";
import { AnimatePresence, motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import Image_1_nl from "@/assets/app/explainer/nl/1@2x.png";
import Image_2_nl from "@/assets/app/explainer/nl/2@2x.png";
import Image_3_nl from "@/assets/app/explainer/nl/3@2x.png";
import Image_4_nl from "@/assets/app/explainer/nl/4@2x.png";
import Image from "@/assets/splash_icon.svg";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { Toolbar } from "@/components/Global/Toolbar";
import { fadeAnimation } from "@/components/Modal/animations";
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
} from "@/components/ui/carousel";
import { arrowRightIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { useMyData } from "@/lib/query/functions/user/data";
import { useSelectorModalStore } from "@/lib/store/modal";
import { cx } from "@/lib/style/cva.config";
import { type Step, getCarouselGroup } from "@/utils/functions/getCarouselGroup";

const MotionButton = motion.create(Button);

const steps: Step[] = [
	{
		image: {
			nl: Image_1_nl,
		},
		key: "intro",
	},
	{
		image: {
			nl: Image_2_nl,
		},
		key: "privacy",
	},
	{
		image: {
			nl: Image_3_nl,
		},
		key: "core-values",
		group: 1,
	},
	{
		image: {
			nl: Image_4_nl,
		},
		key: "perfect-day",
		group: 1,
	},
];

export function ModalAppExplainer() {
	const { t, i18n } = useTranslation();
	const modalRevealRef = useRef<HTMLIonModalElement>(null);
	const modalRef = useRef<HTMLIonModalElement>(null);
	const posthog = usePostHog();
	const modal = useSelectorModalStore.use.appExplainer();
	const present = useSelectorModalStore.use.present();
	const dismiss = useSelectorModalStore.use.dismiss();
	const [, { mutation }] = useMyData();
	const [api, setApi] = useState<CarouselApi>();
	const [scrollProgress, setScrollProgress] = useState(0);
	const deferredScrollProgress = useDeferredValue(scrollProgress);
	const group = getCarouselGroup(steps, scrollProgress);

	function onScroll(api: NonNullable<CarouselApi>) {
		setScrollProgress(api.scrollProgress() * (steps.length - 1));
	}

	useEffect(() => {
		if (!api) {
			return;
		}

		api.on("scroll", onScroll);

		return () => {
			api.off("scroll", onScroll);
		};
	}, [api]);

	useEffect(() => {
		if (!api || !modal.isOpen) {
			return;
		}

		api.reInit();
	}, [api, modal.isOpen]);

	return (
		<>
			<IonModal
				ref={modalRevealRef}
				isOpen={modal.isOpen}
				data-attr="modal.app.explainer.splash"
				onWillPresent={() => {
					present("appExplainer");
					posthog.capture("Modal App Explainer Present");
				}}
				onDidPresent={() => {
					setTimeout(() => {
						modalRevealRef.current?.dismiss();
					}, 4000);
				}}
				onClick={() => modalRevealRef.current?.dismiss()}
				onWillDismiss={() => modalRef.current?.present()}
				enterAnimation={(baseEl) => fadeAnimation(baseEl).direction("normal")}
				leaveAnimation={(baseEl) => fadeAnimation(baseEl).direction("reverse")}
				className="!ion-rounded-none ion-h-full ion-w-full cursor-pointer"
			>
				<Content
					fullscreen
					scrollY={false}
					className="part-background:bg-gradient-to-b part-background:from-[#F0F7FF] part-background:to-[#FFF0E6] part-scroll:items-center part-scroll:justify-center part-scroll:gap-3 part-scroll:text-center part-scroll:text-white"
				>
					<img src={Image} className="drag-none -m-8 w-1/3 max-w-lg min-w-32 select-none" />

					<div className="text-brown-700 -mt-4 text-lg font-bold text-pretty italic">
						{t("modal.app.explainer.splash.motto")
							.split(" ")
							.map((word, index) => (
								<motion.span
									key={word + index}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.2, delay: 1 + 0.2 * index }}
								>
									{word}{" "}
								</motion.span>
							))}
					</div>
				</Content>
			</IonModal>

			<IonModal
				ref={modalRef}
				onWillDismiss={() => {
					dismiss("appExplainer");
					posthog.capture("Modal App Explainer Dismiss");
				}}
				onDidDismiss={() => api?.scrollTo(0, true)}
				backdropDismiss={false}
				initialBreakpoint={1}
				breakpoints={[0, 1]}
				className="ion-h-auto ion-rounded-4xl"
			>
				<Carousel plugins={[AutoHeight()]} setApi={setApi} className="ion-padding -m-4 mt-4 pt-12">
					<CarouselContent className="items-start transition-[height] *:*:p-4">
						{steps.map((step) => (
							<CarouselItem key={step.key}>
								<div className="flex flex-col gap-2">
									<img
										src={step.image[i18n.language]}
										alt={t(`modal.app.explainer.steps.${step.key}.alt`)}
										className="max-h-[50dvh] w-full flex-1 object-contain"
										onLoad={() => api?.reInit()}
									/>

									<div className="flex flex-col gap-3">
										<IonText className="text-brown-700 text-2xl font-semibold">
											{t(`modal.app.explainer.steps.${step.key}.title`)}
										</IonText>
										<IonText className="text-brown-600 text-base">
											{t(`modal.app.explainer.steps.${step.key}.description`)}
										</IonText>
									</div>
								</div>
							</CarouselItem>
						))}
					</CarouselContent>
				</Carousel>

				<IonFooter className="ion-no-border pb-6">
					<Toolbar className="ion-bg-brown-200 ion-p-4">
						<AnimatePresence>
							{group && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									className="relative isolate flex w-fit flex-row items-center justify-center gap-2 py-2 *:size-2.5 *:rounded-full!"
								>
									{Array.from({ length: group.count }).map((_, index) => (
										<button
											key={index}
											className="bg-brown-300"
											onClick={() => api?.scrollTo(index)}
										/>
									))}
									<motion.div
										className="pointer-events-none absolute left-0 z-10 bg-orange-500"
										initial={{ x: "0rem" }}
										animate={{ x: `${group.current * 1.125}rem` }}
										transition={{ type: false }}
									/>
								</motion.div>
							)}
						</AnimatePresence>
						<IonButtons slot="primary" className="relative">
							{steps.map((step, index) => (
								<MotionButton
									key={step.key}
									initial="dynamic"
									animate="dynamic"
									variants={{
										dynamic: {
											opacity: 1 - Math.abs(deferredScrollProgress - index),
											zIndex: Math.floor((1 - Math.abs(deferredScrollProgress - index)) * 10),
										},
									}}
									transition={{ type: false }}
									className={cx("part-native:shadow-xs absolute right-0")}
									data-attr={`modal.app.explainer.step${step}.next`}
									onClick={() => {
										if (api?.canScrollNext()) {
											api.scrollNext();
										} else {
											mutation.mutate((draft) => void (draft.modal.appExplainer.seen = true));
											posthog.capture("Modal App Explainer Finished");
											modal.onCompleted?.();
											modalRef.current?.dismiss();
										}
									}}
								>
									{t(`modal.app.explainer.steps.${step.key}.next`)}
									<IonIcon slot="end" icon={arrowRightIcon} className="size-6 opacity-60" />
								</MotionButton>
							))}
						</IonButtons>
					</Toolbar>
				</IonFooter>
			</IonModal>
		</>
	);
}
